import { alpha, Box, Typography, useTheme } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { BaseWidget } from '@src/App/Components/Widgets/BaseWidget';
import type { AppState } from '@store/store';
import { BarChart3, Palette, Pencil, RefreshCw, SquareStack, Users } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface QuickAction {
    id: string;
    title: string;
    icon: React.ReactNode;
    onClick: () => void;
    highlighted?: boolean;
}

export default function QuickActionWidget() {
    const theme = useTheme();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;

    const quickActions: QuickAction[] = [
        {
            id: 'general',
            title: langStrings.general,
            icon: <BarChart3 size={24} />,
            onClick: () => navigate(`/${ELinks.SETTINGS}`),
            highlighted: true,
        },
        {
            id: 'users-ous',
            title: langStrings.userAndOUs,
            icon: <Users size={24} />,
            onClick: () => navigate(`/${ELinks.SETTINGS_ORGUNITS}`),
        },
        {
            id: 'synchronisation',
            title: langStrings.synchronisation,
            icon: <RefreshCw size={24} />,
            onClick: () => navigate(`/${ELinks.SETTINGS_TIME_SYNC}`),
        },
        {
            id: 'appearance',
            title: langStrings.designTitle,
            icon: <Palette size={24} />,
            onClick: () => navigate(`/${ELinks.SETTINGS_STYLE}`),
        },
    ];

    return (
        <BaseWidget>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    height: { xs: 50, sm: 60, md: 65 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.dark}20 100%)`,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SquareStack size={20} color={theme.palette.primary.main} style={{ marginRight: 8 }} />
                    <Typography variant="body1" component="p" sx={{ fontWeight: 600 }}>
                        Quick Actions
                    </Typography>
                </Box>
                <Pencil size={20} color={theme.palette.primary.main} />
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(auto-fit, minmax(125px, 1fr))',
                        sm: 'repeat(auto-fit, minmax(125px, 1fr))',
                    },
                    gap: 2,
                    p: 2,
                }}
            >
                {quickActions.map((action) => (
                    <Box
                        key={action.id}
                        onClick={action.onClick}
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            border: `2px solid ${action.highlighted ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.2)}`,
                            backgroundColor: action.highlighted
                                ? alpha(theme.palette.primary.main, 0.05)
                                : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            minHeight: { xs: 60, sm: 60 },
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                color: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {action.icon}
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 500,
                                textAlign: 'center',
                                color: theme.palette.text.primary,
                            }}
                        >
                            {action.title}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </BaseWidget>
    );
}
